import { useCallback, useEffect, useState } from "react";
import { Eye, Loader2, Plus, Radar, ScanSearch, Sparkles, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// Ad Spy — monitor competitors' live Facebook/Instagram ads (Meta Ad Library),
// mark winners (30+ days running = proven), and let free AI compare them with
// our own ads into concrete lead-optimization moves.
export const AdSpyTab = () => {
  const [config, setConfig] = useState({ has_token: false, pages: [], last_scan: null, analysis: null, analyzed_at: null });
  const [token, setToken] = useState("");
  const [pageInput, setPageInput] = useState("");
  const [ads, setAds] = useState([]);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all"); // all | ours | competitors | winners

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ad-spy/config");
      setConfig(data);
      const { data: adData } = await api.get("/admin/ad-spy/ads");
      setAds(adData.ads || []);
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const savePages = async (pages, note) => {
    try {
      const { data } = await api.put("/admin/ad-spy/config", { pages });
      setConfig(data);
      if (note) toast.success(note);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    }
  };

  const addPage = async () => {
    if (!pageInput.trim()) return;
    setBusy("resolve");
    try {
      const { data: page } = await api.post("/admin/ad-spy/resolve", { page: pageInput.trim() });
      if (config.pages.some((p) => p.id === page.id)) {
        toast.error("That page is already on the watchlist");
      } else {
        await savePages([...config.pages, { ...page, ours: false }], `${page.name} added to the watchlist`);
      }
      setPageInput("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not resolve that page");
    } finally {
      setBusy("");
    }
  };

  const scan = async () => {
    setBusy("scan");
    try {
      const { data } = await api.post("/admin/ad-spy/scan", {}, { timeout: 120000 });
      toast.success(`Found ${data.found} active ads across ${data.pages_scanned} pages`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Scan failed");
    } finally {
      setBusy("");
    }
  };

  const analyze = async () => {
    setBusy("analyze");
    try {
      const { data } = await api.post("/admin/ad-spy/analyze", {}, { timeout: 200000 });
      setConfig((c) => ({ ...c, analysis: data.analysis, analyzed_at: new Date().toISOString() }));
      toast.success(`AI compared ${data.ours_count} of your ads against ${data.competitor_count} competitor ads`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Analysis failed — try again");
    } finally {
      setBusy("");
    }
  };

  const filtered = ads.filter((a) =>
    filter === "ours" ? a.ours : filter === "competitors" ? !a.ours : filter === "winners" ? a.is_winner : true
  );

  return (
    <div className="space-y-6" data-testid="ad-spy-tab">
      {/* setup + watchlist */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B38B2D]">
            <Eye className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Competitor Ad Spy — Facebook & Instagram</h2>
            <p className="text-xs font-light text-[#91837C]">
              Live ads your competitors run in the UAE, straight from Meta's official Ad Library. Long-running ads are proven winners — borrow their angles, beat their offers.
            </p>
          </div>
        </div>

        {!config.has_token && (
          <div className="mt-5 rounded-xl border border-[#EBE3DA] bg-[#FAF8F5] p-4" data-testid="ad-spy-setup">
            <p className="text-xs font-semibold text-[#1A1615]">One-time free setup (2 minutes):</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs font-light leading-relaxed text-[#5A504B]">
              <li>Open <span className="font-medium text-[#1A1615]">developers.facebook.com</span> → My Apps → Create App (Business type — the same app you use for WhatsApp works)</li>
              <li>Open <span className="font-medium text-[#1A1615]">Graph API Explorer</span> → select your app → User Token → Generate</li>
              <li>Paste the token below — it stays on the server, never in the browser</li>
            </ol>
            <div className="mt-3 flex gap-2">
              <input
                type="password"
                className="input-lux flex-1 text-xs"
                placeholder="Meta user access token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid="ad-spy-token"
              />
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.put("/admin/ad-spy/config", { token });
                    setConfig(data);
                    setToken("");
                    toast.success("Token saved — build your watchlist");
                  } catch {
                    toast.error("Could not save the token");
                  }
                }}
                data-testid="ad-spy-token-save"
                className="btn-gold px-5 py-2 text-[10px]"
              >
                Save Token
              </button>
            </div>
          </div>
        )}

        {config.has_token && (
          <>
            <div className="mt-5 flex gap-2">
              <input
                className="input-lux flex-1 text-xs"
                placeholder="Competitor Facebook page — URL, @name or numeric Page ID"
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && busy !== "resolve" && addPage()}
                data-testid="ad-spy-page-input"
              />
              <button onClick={addPage} disabled={busy === "resolve"} data-testid="ad-spy-page-add" className="btn-gold inline-flex items-center gap-2 px-5 py-2 text-[10px] disabled:opacity-60">
                {busy === "resolve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Watch
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="ad-spy-watchlist">
              {config.pages.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs text-[#5A504B]" data-testid={`ad-spy-page-${p.id}`}>
                  <Radar className="h-3 w-3 text-[#B38B2D]" aria-hidden="true" />
                  {p.name}
                  <button
                    onClick={() => savePages(config.pages.map((x) => (x.id === p.id ? { ...x, ours: !x.ours } : x)))}
                    data-testid={`ad-spy-ours-${p.id}`}
                    className={`rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em] ${p.ours ? "bg-emerald-100 text-emerald-800" : "bg-[#F5EBE6] text-[#91837C] hover:text-[#1A1615]"}`}
                    title="Mark as our own clinic page"
                  >
                    {p.ours ? "Our page" : "Mark ours"}
                  </button>
                  <button
                    onClick={() => savePages(config.pages.filter((x) => x.id !== p.id), "Removed from watchlist")}
                    data-testid={`ad-spy-remove-${p.id}`}
                    className="text-[#C9B8A8] hover:text-red-500"
                    aria-label={`Remove ${p.name}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {!config.pages.length && <p className="text-[10px] font-light text-[#91837C]">Watchlist is empty — add competitor pages above, and mark your own page as "ours" for the comparison.</p>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button onClick={scan} disabled={busy === "scan" || !config.pages.length} data-testid="ad-spy-scan" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
                {busy === "scan" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5" />} Scan Live Ads Now
              </button>
              <button onClick={analyze} disabled={busy === "analyze" || !ads.length} data-testid="ad-spy-analyze" className="btn-outline-lux inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
                {busy === "analyze" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI Compare & Optimize
              </button>
              {config.last_scan && (
                <span className="text-[10px] font-light text-[#91837C]" data-testid="ad-spy-last-scan">
                  Last scan: {new Date(config.last_scan).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* AI comparison */}
      {config.analysis && (
        <div className="glass rounded-2xl p-6" data-testid="ad-spy-analysis">
          <h3 className="font-serif text-lg font-semibold text-[#1A1615]">AI Comparison — how to win cheaper, better leads</h3>
          {config.analyzed_at && (
            <p className="mt-0.5 text-[10px] font-light text-[#91837C]">
              Analyzed {new Date(config.analyzed_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[
              ["What competitors push", config.analysis.competitor_themes, "competitor-themes"],
              ["Winning patterns (30+ day ads)", config.analysis.winning_patterns, "winning-patterns"],
              ["Our gaps", config.analysis.our_gaps, "our-gaps"],
              ["Do this next", config.analysis.recommendations, "recommendations"],
            ].map(([title, items, testid]) => (
              <div key={testid} className="rounded-xl bg-white/70 p-4" data-testid={`ad-spy-${testid}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">{title}</p>
                <ul className="mt-2 space-y-1.5">
                  {(items || []).map((it, i) => (
                    <li key={i} className="text-xs font-light leading-relaxed text-[#5A504B]">· {it}</li>
                  ))}
                  {!(items || []).length && <li className="text-[10px] font-light text-[#91837C]">Nothing detected yet.</li>}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ads grid */}
      {ads.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="ad-spy-results">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-serif text-lg font-semibold text-[#1A1615]">Live Ads ({ads.length})</h3>
            <div className="ml-auto flex gap-1.5">
              {[["all", "All"], ["competitors", "Competitors"], ["ours", "Ours"], ["winners", "Winners 30d+"]].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFilter(id)}
                  data-testid={`ad-spy-filter-${id}`}
                  className={`rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors ${filter === id ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((a, i) => (
              <div key={a.id} className="rounded-xl border border-[#EFE7DF] bg-white/70 p-4" data-testid={`ad-spy-ad-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-[#1A1615]">{a.page_name}</span>
                  {a.ours && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[8px] font-bold uppercase text-emerald-800">Ours</span>}
                  {a.is_winner && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[8px] font-bold uppercase text-[#8a6d1f]" data-testid={`ad-spy-winner-${i}`}>
                      <Trophy className="h-2.5 w-2.5" /> {a.days_running}d
                    </span>
                  )}
                </div>
                <div className="mt-1.5 flex gap-1">
                  {(a.platforms || []).map((p) => (
                    <span key={p} className="rounded-full bg-[#F5EBE6] px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em] text-[#8a6d1f]">{p}</span>
                  ))}
                </div>
                {(a.titles || [])[0] && <p className="mt-2 text-xs font-medium text-[#1A1615]">{a.titles[0]}</p>}
                {(a.bodies || [])[0] && <p className="mt-1 line-clamp-3 text-[11px] font-light leading-relaxed text-[#5A504B]">{a.bodies[0]}</p>}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[9px] font-light text-[#91837C]">Running {a.days_running} day{a.days_running === 1 ? "" : "s"}{a.stop ? " (ended)" : ""}</span>
                  {a.snapshot_url && (
                    <a href={a.snapshot_url} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-[#B38B2D] hover:underline" data-testid={`ad-spy-snapshot-${i}`}>
                      View ad →
                    </a>
                  )}
                </div>
              </div>
            ))}
            {!filtered.length && <p className="text-xs font-light text-[#91837C]">No ads in this view.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
